import { useState, type CSSProperties, type KeyboardEvent, type MouseEvent } from "react";
import { Box, Group, Paper, Stack, Text, UnstyledButton } from "@mantine/core";
import { Link, useNavigate } from "react-router-dom";
import CountryFlag, { countryHasRenderableFlag } from "@/components/shared/CountryFlag";
import CorridorDoubleArrow from "@/components/shared/CorridorDoubleArrow";
import EcosystemHeadShortlistBookmarkButton from "@/pages/ecosystem-heads/components/EcosystemHeadShortlistBookmarkButton";
import SuggestionDismissButton, {
  SUGGESTION_CARD_ACTION_ATTR,
} from "@/pages/dashboard/components/SuggestionDismissButton";
import {
  ECOSYSTEM_CORRIDOR_EXPLORE_CARD_HEIGHT,
  ECOSYSTEM_EXPLORE_CARD_WIDTH,
} from "@/pages/ecosystem-heads/components/ecosystemCorridorPromo.util";
import { getCountryDisplayLabelFromHeadquarters } from "@shared/worldCountryIsoCodes";
import {
  formatTargetCountriesSummary,
  normalizeTargetCountryList,
} from "@/components/ecosystem-head/ecosystemHeadTargetCountries.util";
import type { EcosystemHeadProfileStatus } from "@shared/ecosystemHeadProfile";
import { formatJoinRequestSlotsLabel } from "@/pages/ecosystem-heads/ecosystemHeadCorridorCard.util";
import classes from "@/components/ecosystem-head/EcosystemHeadCorridorCard.module.css";

export type EcosystemHeadCorridorCardData = {
  id: string;
  name: string;
  corridorName?: string;
  managedCompanyCount?: number | null;
  ecosystemIndustry: string | null;
  ecosystemCountry: string | null;
  ecosystemCity?: string | null;
  ecosystemTargetCountry: string | null;
  corridorLabel: string;
  ecosystemHeadSummary?: string | null;
  ecosystemHeadLiveFrom?: string | null;
  profileStatus?: EcosystemHeadProfileStatus;
  expertiseTags?: string[];
  linkStatus?: string | null;
  logoUrl?: string | null;
  joinRequestLimit?: number | null;
  joinRequestUsed?: number;
  joinRequestRemaining?: number | null;
  joinRequestFull?: boolean;
};

type EcosystemHeadCorridorCardProps = {
  head: EcosystemHeadCorridorCardData;
  isSelected?: boolean;
  fullWidth?: boolean;
  /** Navigate via router link (sector carousel, direct profile links). */
  href?: string;
  linkState?: unknown;
  /** Open drawer / custom handler (explore & sector list pages). */
  onClick?: () => void;
  ctaLabel?: string;
  isShortlisted?: boolean;
  showShortlistButton?: boolean;
  onShortlistClick?: () => void | Promise<void>;
  /** Kept for callers; industry/type badges are no longer shown on this card. */
  typeBadge?: string;
  /** Denser type for 3-up dashboard grids. */
  compact?: boolean;
  onDismissClick?: () => void;
};

const CARD_NAVY = "#04305c";
const CARD_BLUE = "#2563EB";

export function CorridorCardThumbnail({
  logoUrl,
  title,
}: {
  logoUrl?: string | null;
  title: string;
}) {
  const initial = title.trim().charAt(0).toUpperCase() || "C";

  return (
    <Box className={classes.thumb} aria-hidden>
      {logoUrl ? (
        <img
          src={logoUrl}
          alt=""
          className={classes.thumbImage}
          style={{
            width: "100%",
            height: "100%",
            maxWidth: "none",
            maxHeight: "none",
            objectFit: "cover",
            objectPosition: "center",
          }}
        />
      ) : (
        <span className={classes.thumbFallback}>{initial}</span>
      )}
    </Box>
  );
}

function formatLocationLabel(country: string | null | undefined): string | null {
  if (!country?.trim()) return null;
  return getCountryDisplayLabelFromHeadquarters(country) ?? country;
}

function formatTargetLocationLabel(targetCountry: string | null | undefined): string | null {
  if (!targetCountry?.trim()) return null;
  const summary = formatTargetCountriesSummary(targetCountry);
  if (!summary) return null;
  if (summary === "All countries" || summary.includes(" more")) return summary;
  const single = normalizeTargetCountryList(targetCountry);
  if (single.length === 1) {
    return getCountryDisplayLabelFromHeadquarters(single[0]) ?? single[0];
  }
  return summary;
}

function renderCountryOnlyRow(
  originCountry: string | null | undefined,
  targetCountry: string | null | undefined,
  compact = false,
) {
  const origin = formatLocationLabel(originCountry);
  const target = formatTargetLocationLabel(targetCountry);

  if (!origin && !target) return null;

  const countryText = origin && target ? `${origin} ↔ ${target}` : (origin ?? target);

  return (
    <Text fz={compact ? 11 : 12} c="#64748B" fw={600} style={{ lineHeight: 1.25 }}>
      {countryText}
    </Text>
  );
}

function CorridorHeaderFlags({
  originCountry,
  targetCountry,
  compact = false,
}: {
  originCountry: string | null | undefined;
  targetCountry: string | null | undefined;
  compact?: boolean;
}) {
  const origin = originCountry?.trim() || null;
  const targetList = normalizeTargetCountryList(targetCountry);
  const target = targetList.length === 1 ? targetList[0] : null;
  const originHasFlag = Boolean(origin && countryHasRenderableFlag(origin));
  const targetHasFlag = Boolean(target && countryHasRenderableFlag(target));
  const flagSize = compact ? "sm" : "md";
  const arrowSize = compact ? 10 : 12;

  if (origin && target && originHasFlag && targetHasFlag) {
    return (
      <Box className={classes.flagPair}>
        <CountryFlag country={origin} size={flagSize} />
        <CorridorDoubleArrow size={arrowSize} color={CARD_BLUE} />
        <CountryFlag country={target} size={flagSize} />
      </Box>
    );
  }

  if (originHasFlag) return <CountryFlag country={origin!} size={flagSize} />;
  if (targetHasFlag) return <CountryFlag country={target!} size={flagSize} />;

  return null;
}

export default function EcosystemHeadCorridorCard({
  head,
  isSelected = false,
  fullWidth = false,
  href,
  linkState,
  onClick,
  ctaLabel = "View",
  isShortlisted = false,
  showShortlistButton = false,
  onShortlistClick,
  compact = false,
  onDismissClick,
}: EcosystemHeadCorridorCardProps) {
  const navigate = useNavigate();
  const [isHovered, setIsHovered] = useState(false);
  const corridorName = head.corridorName?.trim() || head.corridorLabel;
  const countryRow = renderCountryOnlyRow(head.ecosystemCountry, head.ecosystemTargetCountry, compact);
  const joinRequestSlotsLabel = formatJoinRequestSlotsLabel(head);
  const isComingSoon = head.profileStatus === "COMING_SOON";
  const isActive = isHovered || isSelected;
  const isLink = Boolean(href);
  const showBookmark = !isComingSoon && showShortlistButton && Boolean(onShortlistClick);

  const viewProfileButton = isComingSoon ? (
    <Box className={classes.ctaComingSoon}>Coming Soon</Box>
  ) : (
    <Box className={`${classes.ctaButton} ${isActive ? classes.ctaButtonActive : ""}`}>
      {ctaLabel}
    </Box>
  );

  const ctaRow = (
    <Group gap={8} wrap="nowrap" align="center" justify="flex-end">
      {viewProfileButton}
      {onDismissClick ? <SuggestionDismissButton onClick={onDismissClick} /> : null}
      {showBookmark ? (
        <EcosystemHeadShortlistBookmarkButton
          isShortlisted={isShortlisted}
          onClick={() => onShortlistClick?.()}
          variant="outline"
        />
      ) : null}
    </Group>
  );

  const wrapperStyle: CSSProperties = {
    flex: fullWidth ? "1 1 auto" : "0 0 auto",
    width: fullWidth ? "100%" : ECOSYSTEM_EXPLORE_CARD_WIDTH,
    maxWidth: "100%",
    height: fullWidth ? "100%" : ECOSYSTEM_CORRIDOR_EXPLORE_CARD_HEIGHT,
    minHeight: fullWidth ? 0 : ECOSYSTEM_CORRIDOR_EXPLORE_CARD_HEIGHT,
    textAlign: "left",
    cursor: "pointer",
    display: fullWidth ? "flex" : "block",
    flexDirection: fullWidth ? "column" : undefined,
  };

  const cardBody = (
    <Paper
      radius="lg"
      shadow="none"
      h="100%"
      className={`${classes.card} ${compact ? classes.compact : ""}`}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      style={{
        border: `1px solid ${isSelected ? "#60A5FA" : "#E2E8F0"}`,
        boxShadow: isActive
          ? "0 12px 28px rgba(4, 48, 92, 0.14)"
          : "0 4px 14px rgba(15, 23, 42, 0.06)",
        transition: "box-shadow 0.25s ease, border-color 0.25s ease",
        height: fullWidth ? "100%" : undefined,
        minHeight: fullWidth ? 0 : ECOSYSTEM_CORRIDOR_EXPLORE_CARD_HEIGHT,
        flex: fullWidth ? 1 : undefined,
      }}
    >
      <Box className={classes.cardContent}>
        <Group
          wrap="nowrap"
          align="center"
          gap={compact ? 8 : 10}
          px={compact ? 10 : 12}
          py={compact ? 8 : 10}
          style={{ flex: 1, minHeight: 0 }}
        >
          <CorridorCardThumbnail logoUrl={head.logoUrl} title={corridorName} />

          <Stack gap={2} justify="center" style={{ flex: 1, minWidth: 0, minHeight: 0 }}>
            <Text
              fw={compact ? 700 : 800}
              fz={compact ? 12 : 14}
              c={CARD_NAVY}
              lineClamp={2}
              style={{
                lineHeight: 1.25,
                letterSpacing: "0.01em",
                overflowWrap: "normal",
                wordBreak: "normal",
                hyphens: "none",
              }}
            >
              {corridorName}
            </Text>
            {countryRow}
            {joinRequestSlotsLabel ? (
              <Text
                className={classes.slotLabel}
                fz={compact ? 10 : 11}
                fw={600}
                c={head.joinRequestFull ? "#B91C1C" : "#475569"}
                lineClamp={1}
              >
                {joinRequestSlotsLabel}
              </Text>
            ) : null}
          </Stack>

          <Stack gap={8} align="flex-end" justify="center" className={classes.actionsColumn}>
            <CorridorHeaderFlags
              originCountry={head.ecosystemCountry}
              targetCountry={head.ecosystemTargetCountry}
              compact={compact}
            />
            <Box className={classes.ctaRow}>{ctaRow}</Box>
          </Stack>
        </Group>
      </Box>
    </Paper>
  );

  const isInteractive = Boolean(href || onClick);

  const handleCardActivate = () => {
    if (href) {
      navigate(href, { state: linkState });
      return;
    }
    onClick?.();
  };

  const handleCardClick = (event: MouseEvent<HTMLDivElement>) => {
    if ((event.target as HTMLElement).closest(`[${SUGGESTION_CARD_ACTION_ATTR}]`)) return;
    handleCardActivate();
  };

  const handleCardKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key !== "Enter" && event.key !== " ") return;
    event.preventDefault();
    handleCardActivate();
  };

  if ((showBookmark || onDismissClick) && isInteractive) {
    return (
      <Box
        style={wrapperStyle}
        onClick={handleCardClick}
        onKeyDown={handleCardKeyDown}
        role="button"
        tabIndex={0}
      >
        {cardBody}
      </Box>
    );
  }

  if (isLink) {
    return (
      <UnstyledButton component={Link} to={href!} state={linkState} style={wrapperStyle}>
        {cardBody}
      </UnstyledButton>
    );
  }

  return (
    <UnstyledButton onClick={onClick} style={wrapperStyle}>
      {cardBody}
    </UnstyledButton>
  );
}
